/**
 * Create / edit dialog for a rate-limit policy.
 *
 * Shape rules (mirrored from the backend pydantic):
 *   - type=ingress → IngressSource FK (picker), no egress fields
 *   - type=egress  → kind + value (HTTP host or connector instance)
 *   - is_default rows: type + target are read-only (just edit the
 *                      limits + on_breach + description)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { EgressTargetInput } from "@/components/rate-limits/EgressTargetInput";
import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ingressApi } from "@/lib/api-ingress";
import {
  rateLimitsApi,
  type EgressTargetKind,
  type OnBreach,
  type RateLimit,
  type RateLimitType,
} from "@/lib/api-rate-limits";
import { cn } from "@/lib/cn";


export type RateLimitDialogMode =
  | { kind: "create" }
  | { kind: "edit"; rateLimit: RateLimit };


export interface RateLimitDialogProps {
  open: boolean;
  mode: RateLimitDialogMode;
  onOpenChange: (open: boolean) => void;
}


export function RateLimitDialog({
  open,
  mode,
  onOpenChange,
}: RateLimitDialogProps) {
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const editing = mode.kind === "edit" ? mode.rateLimit : null;
  const isDefault = editing?.is_default ?? false;

  const [type, setType] = React.useState<RateLimitType>(
    editing?.type ?? "egress",
  );
  const [description, setDescription] = React.useState(
    editing?.description ?? "",
  );
  const [ingressSourceId, setIngressSourceId] = React.useState<string | null>(
    editing?.ingress_source_id ?? null,
  );
  const [egressKind, setEgressKind] = React.useState<EgressTargetKind>(
    (editing?.egress_target_kind as EgressTargetKind) ?? "http_host",
  );
  const [egressValue, setEgressValue] = React.useState(
    editing?.egress_target_value ?? "",
  );
  const [maxConcurrency, setMaxConcurrency] = React.useState(
    editing?.max_concurrency != null ? String(editing.max_concurrency) : "",
  );
  const [maxQps, setMaxQps] = React.useState(
    editing?.max_qps != null ? String(editing.max_qps) : "",
  );
  const [burst, setBurst] = React.useState(
    editing?.burst != null ? String(editing.burst) : "",
  );
  const [onBreach, setOnBreach] = React.useState<OnBreach>(
    editing?.on_breach ?? "queue",
  );
  const [enabled, setEnabled] = React.useState(editing?.enabled ?? true);
  const [tags, setTags] = React.useState<string[]>(editing?.tags ?? []);

  // Re-seed all the state when the dialog opens with a different target.
  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setType(editing.type);
      setDescription(editing.description ?? "");
      setIngressSourceId(editing.ingress_source_id);
      setEgressKind((editing.egress_target_kind as EgressTargetKind) ?? "http_host");
      setEgressValue(editing.egress_target_value ?? "");
      setMaxConcurrency(
        editing.max_concurrency != null ? String(editing.max_concurrency) : "",
      );
      setMaxQps(editing.max_qps != null ? String(editing.max_qps) : "");
      setBurst(editing.burst != null ? String(editing.burst) : "");
      setOnBreach(editing.on_breach);
      setEnabled(editing.enabled);
      setTags(editing.tags ?? []);
    } else {
      setType("egress");
      setDescription("");
      setIngressSourceId(null);
      setEgressKind("http_host");
      setEgressValue("");
      setMaxConcurrency("");
      setMaxQps("");
      setBurst("");
      setOnBreach("queue");
      setEnabled(true);
      setTags([]);
    }
  }, [open, editing]);

  // Ingress sources for the picker — only fetched when needed.
  const ingressQ = useQuery({
    queryKey: ["rate-limit-dialog", "ingress-sources"],
    queryFn: () => ingressApi.list({ page_size: 100 }),
    enabled: open && type === "ingress" && !isDefault,
    staleTime: 60_000,
  });
  const ingressOptions = React.useMemo(
    () =>
      (ingressQ.data?.items ?? []).map((src) => ({
        value: src.id,
        label: src.name,
        description: src.type,
      })),
    [ingressQ.data],
  );

  const parseNullableInt = (s: string): number | null => {
    const t = s.trim();
    if (!t) return null;
    const n = parseInt(t, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const parseNullableFloat = (s: string): number | null => {
    const t = s.trim();
    if (!t) return null;
    const n = parseFloat(t);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const submit = useMutation<RateLimit, Error>({
    mutationFn: async () => {
      const limits = {
        max_concurrency: parseNullableInt(maxConcurrency),
        max_qps: parseNullableFloat(maxQps),
        burst: parseNullableInt(burst),
        on_breach: onBreach,
        enabled,
        tags,
        description: description.trim() || null,
      };
      if (isCreate) {
        return rateLimitsApi.create({
          type,
          ...limits,
          ingress_source_id: type === "ingress" ? ingressSourceId : null,
          egress_target_kind: type === "egress" ? egressKind : null,
          egress_target_value:
            type === "egress" ? egressValue.trim().toLowerCase() : null,
        });
      }
      // Update: only mutable fields.
      return rateLimitsApi.update(editing!.id, limits);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rate-limits"] });
      onOpenChange(false);
    },
  });

  const canSubmit = (() => {
    if (submit.isPending) return false;
    if (!isCreate) return true; // updating limits — always ok
    if (type === "ingress") return ingressSourceId !== null;
    return egressValue.trim().length > 0;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isCreate
              ? "New rate limit"
              : isDefault
                ? `Edit default ${editing!.type} rate limit`
                : `Edit rate limit`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isDefault && (
            <Alert tone="info" title="Default policy" className="mb-2">
              Applies when no specific rate limit matches a {editing!.type}{" "}
              source/target. Editable but cannot be deleted.
            </Alert>
          )}

          {/* Type picker — only for create + non-default */}
          {isCreate && (
            <div className="space-y-1.5">
              <Label>Direction</Label>
              <div className="flex gap-1.5">
                {(["ingress", "egress"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-xs",
                      type === t
                        ? "border-accent bg-accent/10 text-foreground"
                        : "border-border bg-surface text-muted-foreground hover:border-muted-foreground",
                    )}
                  >
                    {t === "ingress" ? "Ingress (we receive)" : "Egress (we send)"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="rl-description">Description</Label>
            <Input
              id="rl-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === "ingress"
                  ? "e.g. Throttle WMS line updates"
                  : "e.g. Throttle ACME orders API"
              }
            />
          </div>

          {/* Target picker — hidden when editing a default (no target to pick) */}
          {!isDefault && isCreate && (
            <div className="border-t border-border/60 pt-3">
              {type === "ingress" ? (
                <div className="space-y-1.5">
                  <Label>Ingress source</Label>
                  <Combobox
                    options={ingressOptions}
                    value={ingressSourceId}
                    onChange={setIngressSourceId}
                    placeholder={
                      ingressQ.isLoading ? "loading…" : "Pick an ingress source"
                    }
                    loading={ingressQ.isLoading}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    One rate limit per ingress source. If you need to change the
                    source, delete this row and create a new one.
                  </p>
                </div>
              ) : (
                <EgressTargetInput
                  kind={egressKind}
                  onKindChange={setEgressKind}
                  value={egressValue}
                  onValueChange={setEgressValue}
                />
              )}
            </div>
          )}

          {/* Limits */}
          <div className="space-y-3 border-t border-border/60 pt-3">
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Limits
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="rl-conc">Max concurrent</Label>
                <Input
                  id="rl-conc"
                  type="number"
                  min="1"
                  value={maxConcurrency}
                  onChange={(e) => setMaxConcurrency(e.target.value)}
                  placeholder="unlimited"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rl-qps">Max requests/sec</Label>
                <Input
                  id="rl-qps"
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={maxQps}
                  onChange={(e) => setMaxQps(e.target.value)}
                  placeholder="unlimited"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="rl-burst">Burst</Label>
                <Input
                  id="rl-burst"
                  type="number"
                  min="1"
                  value={burst}
                  onChange={(e) => setBurst(e.target.value)}
                  placeholder="= max QPS"
                />
                <p className="text-[10px] text-muted-foreground">
                  Max tokens consumed back-to-back. Defaults to max QPS.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rl-on-breach">When saturated</Label>
                <select
                  id="rl-on-breach"
                  value={onBreach}
                  onChange={(e) => setOnBreach(e.target.value as OnBreach)}
                  className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
                >
                  <option value="queue">Queue (wait turn)</option>
                  <option value="reject">Reject (HTTP 429 / fail fast)</option>
                  <option value="shed">Shed (drop oldest, inbound only)</option>
                </select>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Leave Max concurrent / Max requests/sec empty for{" "}
              <span className="font-mono">unlimited</span> on that dimension.
              Both empty = no throttling.
            </p>
          </div>

          {/* Tags + enabled */}
          <div className="space-y-1.5 border-t border-border/60 pt-3">
            <Label htmlFor="rl-tags">Tags</Label>
            <TagsField id="rl-tags" value={tags} onChange={setTags} />
          </div>

          <label className="flex items-center gap-2 pt-1 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span>Enabled</span>
          </label>

          {submit.error && (
            <Alert tone="danger" title="Save failed">
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {submit.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            )}
            {isCreate ? "Create rate limit" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
